import type {ValidationRegistrar} from 'modules/VendorRegister/types';

import {DatePicker} from '@date-picker';
import {yupResolver} from '@hookform/resolvers/yup';
import {toast} from '@Toast';
import FormInput from 'modules/VendorRegister/components/common/FormInput/FormInput';
import {useCallback, useEffect, useRef, useState} from 'react';
import {useForm} from 'react-hook-form';
import {useAppDispatch} from 'store';
import {sendPersonalInfo, sendSabtahvalInfo} from 'store/slices/vendor-registration/thunk';
import {p2e} from 'utils/Helper';

import {PERSONAL_INFO_SCHEMA} from './constants';

type PersonalInfoForm = {
  nationalCode: string;
  faName: string;
  faFamily: string;
  enName: string;
  enFamily: string;
};

type PersonalInfoProps = {
  registerValidation: ValidationRegistrar;
};

const PersonalInfo: React.FC<PersonalInfoProps> = ({registerValidation}) => {
  const dispatch = useAppDispatch();
  const [birthDate, setBirthDate] = useState('');
  const [showExtraFields, setShowExtraFields] = useState(false);
  const sabtahvalRequestRef = useRef(0);
  const {control, handleSubmit, watch, setValue} = useForm<PersonalInfoForm>({
    resolver: yupResolver(PERSONAL_INFO_SCHEMA),
    mode: 'onChange',
    defaultValues: {
      nationalCode: '',
      faName: '',
      faFamily: '',
      enFamily: '',
      enName: ''
    }
  });
  const nationalCode = watch('nationalCode');

  const submitPersonalInfo = useCallback(
    async (data: PersonalInfoForm) => {
      if (!birthDate) {
        toast('تاریخ تولد را وارد کنید');
        return false;
      }

      try {
        const response = await dispatch(
          sendPersonalInfo({
            birthDate,
            faName: data.faName,
            faFamily: data.faFamily,
            enName: data.enName,
            enFamily: data.enFamily,
            nationalCode: p2e(data.nationalCode)
          })
        ).unwrap();

        if (response.isSuccess) {
          return true;
        }

        toast(response.message || 'خطا');
      } catch (error) {
        toast(
          typeof error === 'object' &&
            error !== null &&
            'message' in error &&
            typeof error.message === 'string'
            ? error.message
            : 'خطا در ذخیره سازی اطلاعات'
        );
      }

      return false;
    },
    [birthDate, dispatch]
  );

  const validateStep = useCallback(() => {
    return new Promise<boolean>((resolve) => {
      void handleSubmit(
        async (data) => {
          resolve(await submitPersonalInfo(data));
        },
        () => resolve(false)
      )();
    });
  }, [handleSubmit, submitPersonalInfo]);

  useEffect(() => {
    registerValidation(validateStep);
  }, [registerValidation, validateStep]);

  useEffect(() => {
    const requestId = ++sabtahvalRequestRef.current;

    if (nationalCode.length !== 10 || !birthDate) {
      setShowExtraFields(false);
      setValue('faName', '');
      setValue('faFamily', '');
      return;
    }

    let cancelled = false;

    void dispatch(
      sendSabtahvalInfo({
        birthDate,
        nationalCode: p2e(nationalCode)
      })
    )
      .unwrap()
      .then((response) => {
        if (cancelled || requestId !== sabtahvalRequestRef.current) {
          return;
        }

        if (response.status === 404 || response.status === 400) {
          setShowExtraFields(false);
          setValue('faName', '');
          setValue('faFamily', '');
          toast(response.message || 'خطا');
          return;
        }

        setValue('faName', response.data.firstName);
        setValue('faFamily', response.data.lastName);
        setShowExtraFields(true);
      })
      .catch(() => {
        if (!cancelled && requestId === sabtahvalRequestRef.current) {
          setShowExtraFields(false);
          setValue('faName', '');
          setValue('faFamily', '');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [birthDate, dispatch, nationalCode, setValue]);

  return (
    <div className="flex flex-col gap-2.5 px-5 py-2.5">
      <FormInput
        name="nationalCode"
        control={control}
        required
        type="tel"
        inputMode="numeric"
        maxLength={10}
        label="کدملی"
        placeholder="کدملی خود وارد کنید"
      />
      <DatePicker
        id="birthDate"
        label="تاریخ تولد"
        required
        format="YYYY-MM-DD"
        onChange={(_, date) => {
          const selectedDate = Array.isArray(date) ? date[0] : date;
          setBirthDate((selectedDate ?? '').replace(/-/g, ''));
        }}
      />
      {showExtraFields ? (
        <>
          <FormInput
            name="faName"
            control={control}
            disabled
            label="نام"
            placeholder="نام خود را وارد کنید"
          />
          <FormInput
            name="faFamily"
            control={control}
            disabled
            label="نام خانوادگی"
            placeholder="نام خانوادگی خود را وارد کنید"
          />
          <FormInput
            name="enName"
            control={control}
            required
            label="نام به انگلیسی"
            placeholder="نام خود را وارد کنید"
          />
          <FormInput
            name="enFamily"
            control={control}
            required
            label="نام خانوادگی به انگلیسی"
            placeholder="نام خانوادگی خود را وارد کنید"
          />
        </>
      ) : null}
    </div>
  );
};

export default PersonalInfo;
